// The call, without React: the protocol client, the mic, Otto's player, the
// local state machine (listening, thinking, speaking, handoff), the barge-in
// duck and the screen wake lock, wired together. The page subscribes to a
// small snapshot that changes only on events a person could notice (never per
// audio frame); the two Ottos read the high-rate values through refs.
//
// Every dependency is injected, so the wiring rules are tested with fakes
// (test/call-controller.test.ts):
//   - `interrupted` fades Otto out over 30 ms and resets the duck (design 4.3)
//   - a turn counts as played only when its `otto_done` arrived AND the player
//     went idle; that number goes out in `resume` (design 4.4)
//   - after `ended`, the page moves on only when Otto's last word has played
//     (the server ends after his last word; this waits for the speakers too)
//   - the owner's End asks the server; the server always has the last word
import type { AgendaItemView, AvatarMood, ClientInfo, EndReason, Mode, ServerEvent } from "@otto/protocol";
import type { MicSettings } from "@otto/config";
import type { LiveClientOptions, LiveStatus } from "./liveClient";
import type { MicCaptureState, MicFrameInfo } from "./audio/micCapture";
import type { MicProblem } from "./audio/micPermission";
import type { MicGateOptions } from "./audio/micGate";
import type { PlayerAudioState, LipSyncAttachment } from "./audio/player";
import { createDuckController, type DuckController } from "./audio/duck";
import type { DuckSettings } from "@otto/config";
import { createOttoStateMachine, type OttoState, type OttoStateMachine, type OttoStateSnapshot } from "./ottoState";
import { appendTranscript } from "./captions";

export type CallPhase =
  "idle" | "starting" | "connecting" | "live" | "reconnecting" | "lost" | "mic_failed" | "failed" | "ending" | "ended";

export interface CallView {
  phase: CallPhase;
  micProblem: MicProblem | null;
  failure: { code: string; message: string } | null;
  endedReason: EndReason | null;
  /** Ended and Otto's last word has played: show the thank-you page. */
  finished: boolean;
  serverRecovering: boolean;
  resumed: boolean;
  agenda: AgendaItemView[];
  targetMinutes: number;
  capMinutes: number;
  agendaIndex: number;
  /** Connected seconds from the server, and when (perf ms) it was received, for a smooth local clock. */
  clock: { seconds: number; receivedAt: number; paused: boolean };
  extension: { offered: boolean; accepted: boolean | null; answered: boolean | null };
  mode: Mode;
  muted: boolean;
  micState: MicCaptureState;
  audioBlocked: boolean;
  ottoState: OttoState;
  caption: { turn: number | null; text: string; startedAt: number | null };
  reconnectAttempt: number;
  /** The stage's expression cue from the server (agenda.stage_moods, design 7.2); "neutral" when none. */
  mood: AvatarMood;
}

export interface PlayerLike {
  readonly levelRef: { current: number };
  readonly blendshapeRef: { current: Record<string, number> | null };
  readonly playing: boolean;
  readonly outputRoute: "bluetooth" | "unknown";
  init(): Promise<boolean>;
  play(pcm: ArrayBuffer): boolean;
  duck(gain: number, ms?: number): void;
  unduck(ms?: number): void;
  fadeOut(ms?: number): Promise<void>;
  clear(): void;
  resume(): Promise<boolean>;
  setLipSync(lipSync: LipSyncAttachment | null): void;
  close(): void;
}

export interface MicLike {
  start(): Promise<{ ok: true } | { ok: false; problem: MicProblem }>;
  setMuted(muted: boolean): void;
  resume(): Promise<boolean>;
  stop(): void;
}

export interface ClientLike {
  readonly isLive: boolean;
  start(): void;
  reconnectNow(): void;
  checkStale(staleMs: number): void;
  sendAudio(pcm: ArrayBuffer): boolean;
  setMode(mode: Mode): void;
  answerExtension(accept: boolean): boolean;
  sendTelemetry(event: {
    avatar_load_ms?: number;
    fps_avg?: number;
    webgl_context_lost?: boolean;
    fell_back_to_2d?: boolean;
    output_route?: "wired" | "bluetooth" | "speaker" | "unknown";
    heard_turn?: number;
    heard_ms?: number;
    rtt_ms?: number;
  }): boolean;
  end(reason: "owner_ended" | "withdrew_consent" | "page_unload"): void;
  close(): void;
}

export interface CallDeps {
  createPlayer(options: {
    onPlaying: (playing: boolean) => void;
    onAudioState: (state: PlayerAudioState) => void;
    lipSyncDelayMs?: number;
  }): PlayerLike;
  createMic(options: {
    gate: MicGateOptions;
    settings?: MicSettings;
    send: (pcm: ArrayBuffer) => void;
    echoLevel?: () => number;
    echoPending?: () => boolean;
    onFrame: (info: MicFrameInfo) => void;
    onState: (state: MicCaptureState, problem?: MicProblem) => void;
  }): MicLike;
  createClient(options: LiveClientOptions): ClientLike;
  wakeLock: { acquire(): Promise<boolean>; release(): void };
  /** Wall clock, ms. */
  now(): number;
  /** Monotonic clock, ms (performance.now). */
  perfNow(): number;
  schedule(fn: () => void, ms: number): unknown;
  cancel(handle: unknown): void;
  /** Persist what a reload needs (session.ts). */
  remember(patch: { started?: boolean; last_seen_at?: number; last_turn_played?: number | null; mode?: Mode }): void;
}

export interface CallSetup {
  consultId: string;
  liveUrl: string;
  liveToken: string;
  mode: Mode;
  agenda: AgendaItemView[];
  targetMinutes: number;
  capMinutes: number;
  /**
   * A reload of a call already under way: when the page last saw the line up
   * (epoch ms). `resume` then carries the gap measured when the call restarts,
   * not when the page loaded (the owner may sit on the Rejoin screen a while).
   */
  resumeFromLastSeenAt: number | null;
  lastTurnPlayed: number | null;
  client: ClientInfo;
  gate: MicGateOptions;
  micSettings?: MicSettings;
  duck: DuckSettings;
  duckEnabled: boolean;
  lipSyncDelayMs?: number;
}

/** How long to wait for `ended` after the owner's End before moving on anyway. */
/**
 * How long after an audio chunk arrives Otto counts as audible even before the player reports playing (Oct 2 2026:
 * the report comes a few frames after his first sound). Chunks stream ahead of playback, so this only bridges the start.
 */
export const OTTO_AUDIO_PENDING_MS = 400;
export const END_WAIT_MS = 25_000;
/** How long to wait for Otto's last word to finish playing after `ended`. */
export const GOODBYE_MAX_WAIT_MS = 15_000;
/** Remember "still here" for a reload this often. */
const SEEN_EVERY_MS = 5_000;

export class CallController {
  readonly stateMachine: OttoStateMachine;
  readonly ottoStateRef: { current: OttoStateSnapshot };
  readonly micLevelRef = { current: 0 };
  readonly micBandsRef: { current: number[] } = { current: [0, 0, 0, 0, 0, 0] };
  readonly player: PlayerLike;

  private readonly setup: CallSetup;
  private readonly deps: CallDeps;
  private readonly duck: DuckController;
  private mic: MicLike | null = null;
  private client: ClientLike | null = null;
  private listeners = new Set<() => void>();
  private view: CallView;
  private lastTurnPlayed: number | null;
  private lastAudioTurn: number | null = null;
  private doneTurns = new Set<number>();
  private captionTurnPending: number | null = null;
  // What the owner waited (Oct 1 2026): their last word, measured here, to the first sound of Otto's reply.
  private lastOwnerVoiceAt = Number.NEGATIVE_INFINITY;
  private lastOttoStoppedAt = Number.NEGATIVE_INFINITY;
  private lastHeardTurn: number | null = null;
  private lastRttMs: number | null = null;
  private lastOttoAudioAt = Number.NEGATIVE_INFINITY;
  private lastSeenSaved = 0;
  private endTimer: unknown = null;
  private goodbyeTimer: unknown = null;
  private tickTimer: unknown = null;
  private disposed = false;
  private started = false;

  constructor(setup: CallSetup, deps: CallDeps) {
    this.setup = setup;
    this.deps = deps;
    this.lastTurnPlayed = setup.lastTurnPlayed;
    this.stateMachine = createOttoStateMachine(
      { voiceThreshold: setup.gate.threshold > 0 ? setup.gate.threshold : 0.005 },
      deps.perfNow(),
    );
    this.ottoStateRef = { current: this.stateMachine.snapshot };
    this.duck = createDuckController(setup.duck);
    this.player = deps.createPlayer({
      onPlaying: (playing) => this.onPlaying(playing),
      onAudioState: (state) => this.patch({ audioBlocked: state === "blocked" }),
      ...(setup.lipSyncDelayMs !== undefined ? { lipSyncDelayMs: setup.lipSyncDelayMs } : {}),
    });
    this.view = {
      phase: "idle",
      micProblem: null,
      failure: null,
      endedReason: null,
      finished: false,
      serverRecovering: false,
      resumed: false,
      agenda: setup.agenda,
      targetMinutes: setup.targetMinutes,
      capMinutes: setup.capMinutes,
      agendaIndex: 0,
      clock: { seconds: 0, receivedAt: deps.perfNow(), paused: true },
      extension: { offered: false, accepted: null, answered: null },
      mode: setup.mode,
      muted: false,
      micState: "idle",
      audioBlocked: false,
      ottoState: "idle",
      caption: { turn: null, text: "", startedAt: null },
      reconnectAttempt: 0,
      mood: "neutral",
    };
  }

  // ---- subscription (useSyncExternalStore) ----

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): CallView => this.view;

  private patch(next: Partial<CallView>) {
    this.view = { ...this.view, ...next };
    for (const listener of this.listeners) listener();
  }

  // ---- the owner's actions ----

  /** Start the call. Call from a tap (or right after the check-in tap), after primeCallAudio(). */
  async start(): Promise<void> {
    if (this.started || this.disposed) return;
    this.started = true;
    this.patch({ phase: "starting", micProblem: null, failure: null });
    this.mic = this.deps.createMic({
      gate: this.setup.gate,
      ...(this.setup.micSettings ? { settings: this.setup.micSettings } : {}),
      send: (pcm) => {
        this.client?.sendAudio(pcm);
      },
      // The echo guard's reference: what the speakers play now (levelRef is RMS x 4, clamped at 1).
      echoLevel: () => (this.player.playing ? this.player.levelRef.current / 4 : 0),
      // From Otto's first audio chunk, before the player says it is playing (Oct 2 2026).
      echoPending: () => this.ottoAudible(),
      onFrame: (info) => this.onMicFrame(info),
      onState: (state, problem) => {
        this.patch({ micState: state, ...(problem ? { micProblem: problem } : {}) });
      },
    });
    const [playerOk, mic] = await Promise.all([this.player.init(), this.mic.start()]);
    if (this.disposed) return;
    if (!mic.ok) {
      // Design 7.8: detected before Otto greets anyone; the page shows help for this browser.
      this.started = false;
      this.mic.stop();
      this.mic = null;
      this.patch({ phase: "mic_failed", micProblem: mic.problem });
      return;
    }
    if (!playerOk) {
      this.patch({ audioBlocked: true });
    }
    // The greeting waits only for a live mic and some Otto on screen (the 2D card
    // is already there), never for the 3D file (design 3.4 step 4).
    this.client = this.deps.createClient({
      url: this.setup.liveUrl,
      consultId: this.setup.consultId,
      liveToken: this.setup.liveToken,
      mode: this.view.mode,
      client: { ...this.setup.client, output_route: this.player.outputRoute },
      resumeFrom:
        this.setup.resumeFromLastSeenAt !== null
          ? { gapMs: Math.max(0, this.deps.now() - this.setup.resumeFromLastSeenAt) }
          : null,
      lastTurnPlayed: () => this.lastTurnPlayed,
      handlers: {
        onStatus: (status) => this.onStatus(status),
        onEvent: (event) => this.onEvent(event),
        onAudio: (chunk, turn) => this.onAudio(chunk, turn),
        onRtt: (ms) => {
          this.lastRttMs = Math.round(ms);
        },
        onProtocolError: (detail) => console.error("[otto] live protocol message refused:", detail),
      },
    });
    this.patch({ phase: "connecting" });
    this.client.start();
    void this.deps.wakeLock.acquire();
    this.startTicker();
  }

  /** A tap after a long drop ("lost"), or on return from a hidden tab. */
  async rejoin(): Promise<void> {
    await Promise.all([this.player.resume(), this.mic?.resume() ?? Promise.resolve(false)]);
    if (this.view.phase === "mic_failed") {
      await this.start();
      return;
    }
    this.client?.reconnectNow();
  }

  /** The page came back to the front (phone unlocked, tab switched back). */
  async onVisible(): Promise<void> {
    if (this.disposed || !this.client) return;
    const [audio] = await Promise.all([this.player.resume(), this.mic?.resume() ?? Promise.resolve(false)]);
    if (!audio) this.patch({ audioBlocked: true });
    // A socket quiet this long is not trusted even if it says OPEN.
    this.client.checkStale(20_000);
    if (this.view.phase === "reconnecting") this.client.reconnectNow();
  }

  onOnline(): void {
    if (this.view.phase === "reconnecting" || this.view.phase === "lost") this.client?.reconnectNow();
  }

  async unlockAudio(): Promise<void> {
    const ok = await this.player.resume();
    this.patch({ audioBlocked: !ok });
  }

  setMuted(muted: boolean): void {
    this.mic?.setMuted(muted);
    this.stateMachine.setMuted(muted, this.deps.perfNow());
    this.patch({ muted, ottoState: this.stateMachine.snapshot.state });
  }

  setMode(mode: Mode): void {
    if (mode === this.view.mode) return;
    this.client?.setMode(mode);
    this.deps.remember({ mode });
    this.patch({ mode });
  }

  answerExtension(accept: boolean): void {
    this.client?.answerExtension(accept);
    this.patch({ extension: { ...this.view.extension, answered: accept } });
  }

  /** The End button. The server ends the call after Otto's last word. */
  end(): void {
    if (this.view.phase === "ended" || this.view.phase === "ending") return;
    if (!this.client) {
      this.finish(null);
      return;
    }
    this.client.end("owner_ended");
    this.patch({ phase: "ending" });
    this.endTimer = this.deps.schedule(() => {
      // No `ended` in time (a dead line): move on; the server marks the consult on its side.
      this.finish(null);
    }, END_WAIT_MS);
  }

  sendTelemetry(event: Parameters<ClientLike["sendTelemetry"]>[0]): void {
    this.client?.sendTelemetry({ ...event, output_route: this.player.outputRoute });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.endTimer !== null) this.deps.cancel(this.endTimer);
    if (this.goodbyeTimer !== null) this.deps.cancel(this.goodbyeTimer);
    if (this.tickTimer !== null) this.deps.cancel(this.tickTimer);
    this.client?.close();
    this.mic?.stop();
    this.player.close();
    this.deps.wakeLock.release();
    this.listeners.clear();
  }

  // ---- events ----

  private onStatus(status: LiveStatus) {
    switch (status.kind) {
      case "connecting":
        if (this.view.phase === "ending") break;
        this.patch({ phase: status.reconnect ? "reconnecting" : "connecting", reconnectAttempt: status.attempt });
        break;
      case "live":
        this.deps.remember({ started: true, last_seen_at: this.deps.now() });
        this.patch({
          phase: this.view.phase === "ending" ? "ending" : "live",
          resumed: status.resumed,
          reconnectAttempt: 0,
        });
        break;
      case "reconnecting":
        this.patch({
          phase: this.view.phase === "ending" ? "ending" : "reconnecting",
          reconnectAttempt: status.attempt,
        });
        break;
      case "lost":
        this.patch({ phase: "lost" });
        break;
      case "ended":
        this.onEnded(status.reason);
        break;
      case "failed":
        this.mic?.stop();
        this.deps.wakeLock.release();
        this.patch({ phase: "failed", failure: { code: status.code, message: status.message } });
        break;
      default:
        break;
    }
  }

  private onEvent(event: ServerEvent) {
    switch (event.type) {
      case "ready":
        this.patch({
          agenda: event.agenda.length ? event.agenda : this.view.agenda,
          targetMinutes: event.target_minutes,
          capMinutes: event.cap_minutes,
          mode: event.mode,
          serverRecovering: false,
        });
        break;
      case "state":
        this.patch({
          agendaIndex: event.agenda_index,
          clock: { seconds: event.clock_seconds, receivedAt: this.deps.perfNow(), paused: event.paused },
          extension: {
            offered: event.extension_offered,
            accepted: event.extension_accepted,
            // A server answer (by voice or tap) replaces the local one.
            answered: event.extension_accepted !== null ? event.extension_accepted : this.view.extension.answered,
          },
          mood: event.mood ?? "neutral",
        });
        break;
      case "otto_speaking":
        this.lastAudioTurn = event.turn;
        this.captionTurnPending = event.turn;
        if (this.view.caption.turn !== event.turn)
          this.patch({ caption: { turn: event.turn, text: "", startedAt: null } });
        break;
      case "output_transcript": {
        const current = this.view.caption.turn === event.turn ? this.view.caption.text : "";
        const startedAt = this.view.caption.turn === event.turn ? this.view.caption.startedAt : null;
        this.patch({ caption: { turn: event.turn, text: appendTranscript(current, event.text), startedAt } });
        break;
      }
      case "otto_done":
        this.doneTurns.add(event.turn);
        if (!this.player.playing) this.markPlayed(event.turn);
        break;
      case "interrupted":
        // Design 4.3: the owner barged in. Fade out over 30 ms, reset the mouth and the duck.
        this.doneTurns.add(event.turn);
        this.duck.reset();
        void this.player.fadeOut().then(() => this.markPlayed(event.turn));
        break;
      case "reconnecting":
        this.patch({ serverRecovering: true });
        break;
      case "reconnected":
        this.patch({ serverRecovering: false });
        break;
      default:
        break;
    }
  }

  private onAudio(chunk: ArrayBuffer, turn: number) {
    this.lastAudioTurn = turn;
    this.lastOttoAudioAt = this.deps.perfNow();
    if (!this.player.play(chunk)) this.patch({ audioBlocked: true });
  }

  private onPlaying(playing: boolean) {
    const now = this.deps.perfNow();
    this.stateMachine.setOttoPlaying(playing, now);
    if (playing) this.reportHeard(now);
    else this.lastOttoStoppedAt = now;
    if (playing && this.captionTurnPending !== null && this.view.caption.turn === this.captionTurnPending) {
      // Captions pace from the moment Otto's sound starts (design 7.6).
      this.captionTurnPending = null;
      this.patch({ caption: { ...this.view.caption, startedAt: now } });
    }
    if (!playing) {
      this.duck.reset();
      if (this.lastAudioTurn !== null && this.doneTurns.has(this.lastAudioTurn)) this.markPlayed(this.lastAudioTurn);
      if (this.view.phase === "ended") this.finishWhenDrained();
    }
    this.syncOttoState();
  }

  private onMicFrame(info: MicFrameInfo) {
    const now = this.deps.perfNow();
    this.micLevelRef.current = info.level;
    this.micBandsRef.current = info.bands;
    this.stateMachine.micFrame(info.rms, now);
    // The owner's voice, while Otto is quiet (over Otto it could be his own echo). Twice the gate, at least 0.01.
    if (!this.ottoAudible() && info.rms >= Math.max(0.01, 2 * this.setup.gate.threshold)) this.lastOwnerVoiceAt = now;
    if (this.setup.duckEnabled) {
      const action = this.duck.frame(info.rms, this.player.playing, info.audioMs);
      if (action === "duck") this.player.duck(this.setup.duck.volume);
      else if (action === "restore") this.player.unduck();
    }
    this.syncOttoState();
  }

  /** Once per Otto turn that answered the owner: how long they waited, as heard on this device. */
  private reportHeard(now: number) {
    const turn = this.lastAudioTurn;
    if (turn === null || turn === this.lastHeardTurn || !(this.lastOwnerVoiceAt > this.lastOttoStoppedAt)) return;
    const ms = Math.round(now - this.lastOwnerVoiceAt);
    if (ms < 0 || ms > 60_000) return;
    this.lastHeardTurn = turn;
    this.sendTelemetry({ heard_turn: turn, heard_ms: ms, ...(this.lastRttMs !== null ? { rtt_ms: Math.min(60_000, this.lastRttMs) } : {}) });
  }

  /** Otto is playing, or his audio arrived in the last moment and is about to (the player reports a little late). */
  private ottoAudible(): boolean {
    return this.player.playing || this.deps.perfNow() - this.lastOttoAudioAt < OTTO_AUDIO_PENDING_MS;
  }

  private syncOttoState() {
    const state = this.stateMachine.snapshot.state;
    if (state !== this.view.ottoState) this.patch({ ottoState: state });
  }

  private markPlayed(turn: number) {
    if (this.lastTurnPlayed !== null && turn <= this.lastTurnPlayed) return;
    this.lastTurnPlayed = turn;
    this.deps.remember({ last_turn_played: turn });
  }

  private onEnded(reason: EndReason) {
    if (this.endTimer !== null) this.deps.cancel(this.endTimer);
    this.endTimer = null;
    this.mic?.stop();
    this.deps.wakeLock.release();
    this.patch({ phase: "ended", endedReason: reason });
    this.finishWhenDrained();
  }

  private finishWhenDrained() {
    if (this.view.finished) return;
    if (!this.player.playing) {
      this.finish(this.view.endedReason);
      return;
    }
    if (this.goodbyeTimer === null) {
      this.goodbyeTimer = this.deps.schedule(() => this.finish(this.view.endedReason), GOODBYE_MAX_WAIT_MS);
    }
  }

  private finish(reason: EndReason | null) {
    if (this.view.finished) return;
    if (this.goodbyeTimer !== null) this.deps.cancel(this.goodbyeTimer);
    this.goodbyeTimer = null;
    this.client?.close();
    this.mic?.stop();
    this.deps.wakeLock.release();
    this.patch({ phase: "ended", endedReason: reason ?? this.view.endedReason, finished: true });
  }

  // Once a second: expire the handoff and thinking looks, remember "still here" for a reload.
  private startTicker() {
    const tick = () => {
      this.tickTimer = null;
      if (this.disposed) return;
      this.stateMachine.tick(this.deps.perfNow());
      this.syncOttoState();
      const now = this.deps.now();
      if (this.view.phase === "live" && now - this.lastSeenSaved >= SEEN_EVERY_MS) {
        this.lastSeenSaved = now;
        this.deps.remember({ last_seen_at: now });
      }
      this.tickTimer = this.deps.schedule(tick, 250);
    };
    this.tickTimer = this.deps.schedule(tick, 250);
  }
}
